import * as React from "react";
import { useAppSelector } from "../../../store/hooks";
import { Button, Input } from "antd";
import { formSet, formSetValue, formTriggerValidation, useCpFormError, useCpFormAnyValue, useCpFormTouched, useCpFormSimulateTouched, useCpFormWarning } from "./utils";
import { cloneDeep, get } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { ArrayString } from "../../../schema/types/org/input";
import { store } from "../../../store";
import { CpErrorWarning } from "./CpErrorWarning";
import { CpFormItem } from "./CpFormItem";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { PlusCircleOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { CpLabelDirtyIndicator } from "./CpLabelDirtyIndicator";
import { Only } from "../../workload/draft/only";

interface DirtyIndicatorConfig {
  isEnabled: boolean;
  only?: {
    name: string;
    checkFn: (value: any) => boolean;
  };
}

interface Props {
  label?: string;
  name: string;
  noMargin?: boolean;
  dirtyIndicator?: DirtyIndicatorConfig;
}

export const CpArrayMultilineString: React.FC<Props> = ({ label, name, noMargin, dirtyIndicator }) => {
  const { id } = useCpFormContext();
  const items: ArrayString[] = useAppSelector((state) => get(state, `form.instances.${id}.values.${name}`)) || [];

  const renderDirtyIndicator = () => {
    if (!dirtyIndicator?.isEnabled) return null;

    const indicator = <CpLabelDirtyIndicator name={name} />;

    if (dirtyIndicator.only) {
      return (
        <Only name={dirtyIndicator.only.name} checkFn={dirtyIndicator.only.checkFn}>
          {indicator}
        </Only>
      );
    }

    return indicator;
  };

  function add() {
    const _items = cloneDeep(items);

    const newIndex = _items.length;
    _items.push({ id: uuidv4(), value: "" });
    formSet(id, `values.${name}`, _items);

    formSet(id, `fieldStates.${name}.${newIndex}.value.simulateTouched`, true);

    const form = store.getState().form.instances[id];
    const onBlurCounter = form.meta.onBlurCounter;
    formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
  }

  return (
    <CpFormItem noMargin={noMargin}>
      {label ? (
        <div>
          {label}
          {renderDirtyIndicator()}
        </div>
      ) : null}
      {items.length < 0 ? <div>No Items</div> : null}
      {items.map((item, index) => {
        return <CpArrayMultilineStringItem key={item.id} item={item} index={index} name={name} />;
      })}
      <Button block type={`dashed`} onClick={() => add()} iconPosition="start" icon={<PlusCircleOutlined />}>
        Add
      </Button>
      <CpErrorWarning name={`${name}root`} />
    </CpFormItem>
  );
};

interface ItemProps {
  name: string;
  item: ArrayString;
  index: number;
}

const CpArrayMultilineStringItem: React.FC<ItemProps> = React.memo(({ name, item, index }) => {
  const { id: formId } = useCpFormContext();

  const value = useAppSelector((state) => get(state, `form.instances.${formId}.values.${name}.${index}.value`)) || "";
  const fieldStatePath = `fieldStates.${name}.${index}.value`;

  const submitted = useCpFormAnyValue(`meta.submitted`);
  const simulateTouched = useCpFormSimulateTouched(`${name}.${index}.value`);
  const touched = useCpFormTouched(`${name}.${index}.value`);
  const error = useCpFormError(`${name}.${index}.value`);
  const warning = useCpFormWarning(`${name}.${index}.value`);

  const canShowErrorWarningStatus = React.useMemo(() => {
    return submitted || simulateTouched || touched;
  }, [submitted, simulateTouched, touched]);

  function remove() {
    const form = store.getState().form.instances[formId];
    const values = form.values;

    const items = get(values, name) || [];
    let _items = cloneDeep(items);
    _items = _items.filter((i: ArrayString) => i.id !== item.id);
    formSetValue(formId, `${name}`, _items);

    formTriggerValidation(formId);
  }

  return (
    <div key={item.id} className="mb-1">
      <div className="flex items-start gap-2">
        <Input.TextArea
          id={`${name}.${index}.value`}
          value={value}
          rows={2}
          status={canShowErrorWarningStatus ? (error ? "error" : warning ? "warning" : undefined) : undefined}
          onChange={(e) => {
            formSetValue(formId, `${name}.${index}.value`, e.target.value);
            formSet(formId, `${fieldStatePath}.modified`, true);

            const form = store.getState().form.instances[formId];
            const onChangeCounter = form.meta.onChangeCounter;
            formSet(formId, `meta.onChangeCounter`, onChangeCounter + 1);
          }}
          onFocus={() => {
            formSet(formId, `${fieldStatePath}.focused`, true);
            formSet(formId, `${fieldStatePath}.visited`, true);
          }}
          onBlur={() => {
            formSet(formId, `${fieldStatePath}.focused`, false);
            formSet(formId, `${fieldStatePath}.touched`, true);

            const form = store.getState().form.instances[formId];
            const onBlurCounter = form.meta.onBlurCounter;
            formSet(formId, `meta.onBlurCounter`, onBlurCounter + 1);
          }}
        />
        <Button type={`text`} danger onClick={remove} icon={<MinusCircleOutlined />}></Button>
      </div>
    </div>
  );
});
